/**
 * mSTEM Robot Edit Form (GEN-03). Inline form rendered inside the
 * RobotManagerModal for both Add (initialRobot=null) and Edit
 * (initialRobot=existing) flows.
 *
 * Validation:
 *  - name trimmed; empty name aborts submit silently (button can stay
 *    enabled because the form is small and gives instant feedback).
 *  - maxLength={100} attribute on the input pre-truncates per T-02-03-01.
 *  - id stays stable on edit; for create, slugify(name) becomes the id.
 */
import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {FormattedMessage, defineMessages, injectIntl, intlShape} from 'react-intl';

import styles from './robot-manager-modal.css';

const messages = defineMessages({
    labelName: {
        defaultMessage: 'Tên robot',
        description: 'Form label — robot name',
        id: 'gen.robot.form.label.name'
    },
    labelType: {
        defaultMessage: 'Loại',
        description: 'Form label — robot type',
        id: 'gen.robot.form.label.type'
    },
    labelFw: {
        defaultMessage: 'Firmware',
        description: 'Form label — firmware version',
        id: 'gen.robot.form.label.firmware'
    },
    labelStatus: {
        defaultMessage: 'Trạng thái',
        description: 'Form label — connection status',
        id: 'gen.robot.form.label.status'
    },
    statusReady: {
        defaultMessage: 'Sẵn sàng',
        description: 'Status — ready',
        id: 'gen.robot.status.ready'
    },
    statusOutdated: {
        defaultMessage: 'Cần cập nhật',
        description: 'Status — outdated',
        id: 'gen.robot.status.outdated'
    },
    statusBeta: {
        defaultMessage: 'Bản thử nghiệm',
        description: 'Status — beta',
        id: 'gen.robot.status.beta'
    },
    save: {
        defaultMessage: 'Lưu',
        description: 'Button — save',
        id: 'gen.robot.form.save'
    },
    cancel: {
        defaultMessage: 'Huỷ',
        description: 'Button — cancel',
        id: 'gen.robot.form.cancel'
    }
});

const slugify = function (text) {
    return String(text || '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .substring(0, 64) || `robot-${Date.now()}`;
};

class RobotEditForm extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleChangeName',
            'handleChangeType',
            'handleChangeFw',
            'handleChangeStatus',
            'handleSubmit',
            'handleCancel'
        ]);
        const r = props.initialRobot || {};
        this.state = {
            name: r.name || '',
            type: r.type || 'arduinoUno',
            firmwareVersion: r.firmwareVersion || '1.0.0',
            connectionStatus: r.connectionStatus || 'ready'
        };
    }
    handleChangeName (e) {
        this.setState({name: e.target.value});
    }
    handleChangeType (e) {
        this.setState({type: e.target.value});
    }
    handleChangeFw (e) {
        this.setState({firmwareVersion: e.target.value});
    }
    handleChangeStatus (e) {
        this.setState({connectionStatus: e.target.value});
    }
    handleSubmit (e) {
        if (e && e.preventDefault) e.preventDefault();
        const name = this.state.name.trim();
        if (!name) return;
        const existing = this.props.initialRobot;
        const robot = {
            id: existing ? existing.id : slugify(name),
            name: name,
            type: this.state.type.trim() || 'arduinoUno',
            firmwareVersion: this.state.firmwareVersion.trim() || '0.0.0',
            connectionStatus: this.state.connectionStatus,
            image: existing ? existing.image : null,
            brand: existing ? existing.brand : 'MBF'
        };
        this.props.onSave(robot);
    }
    handleCancel () {
        this.props.onCancel();
    }
    render () {
        const {intl} = this.props;
        return (
            <form
                className={styles.editForm}
                onSubmit={this.handleSubmit}
            >
                <label className={styles.formField}>
                    <span className={styles.formLabel}>
                        <FormattedMessage {...messages.labelName} />
                    </span>
                    <input
                        className={styles.formInput}
                        maxLength={100}
                        type="text"
                        value={this.state.name}
                        onChange={this.handleChangeName}
                    />
                </label>
                <label className={styles.formField}>
                    <span className={styles.formLabel}>
                        <FormattedMessage {...messages.labelType} />
                    </span>
                    <input
                        className={styles.formInput}
                        maxLength={50}
                        type="text"
                        value={this.state.type}
                        onChange={this.handleChangeType}
                    />
                </label>
                <label className={styles.formField}>
                    <span className={styles.formLabel}>
                        <FormattedMessage {...messages.labelFw} />
                    </span>
                    <input
                        className={styles.formInput}
                        maxLength={32}
                        type="text"
                        value={this.state.firmwareVersion}
                        onChange={this.handleChangeFw}
                    />
                </label>
                <label className={styles.formField}>
                    <span className={styles.formLabel}>
                        <FormattedMessage {...messages.labelStatus} />
                    </span>
                    <select
                        className={styles.formInput}
                        value={this.state.connectionStatus}
                        onChange={this.handleChangeStatus}
                    >
                        <option value="ready">{intl.formatMessage(messages.statusReady)}</option>
                        <option value="outdated">{intl.formatMessage(messages.statusOutdated)}</option>
                        <option value="beta">{intl.formatMessage(messages.statusBeta)}</option>
                    </select>
                </label>
                <div className={styles.formActions}>
                    <button
                        className={styles.btnSecondary}
                        type="button"
                        onClick={this.handleCancel}
                    >
                        <FormattedMessage {...messages.cancel} />
                    </button>
                    <button
                        className={styles.btnPrimary}
                        type="submit"
                    >
                        <FormattedMessage {...messages.save} />
                    </button>
                </div>
            </form>
        );
    }
}

RobotEditForm.propTypes = {
    initialRobot: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        type: PropTypes.string,
        firmwareVersion: PropTypes.string,
        connectionStatus: PropTypes.string,
        image: PropTypes.string,
        brand: PropTypes.string
    }),
    intl: intlShape.isRequired,
    onCancel: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired
};

export default injectIntl(RobotEditForm);
